import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
  ListItemButton,
  Grid,
  Pagination,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MainLayout from '../components/MainLayout';
import { Tabs } from '../components/ui/Tabs';
import { PageHeader } from '../components/ui/PageHeader';
import { Dropdown } from '../components/ui/Dropdown';
import { useAppSelector } from '../hooks/redux';
import PersonaDictation from '../components/playbookBuilder/PersonaDictation';
import PersonaReview from '../components/playbookBuilder/PersonaReview';
import ReadinessGate from '../components/playbookBuilder/ReadinessGate';
import InstructionsPreviewDialog from '../components/playbookBuilder/InstructionsPreviewDialog';
import {
  draftAgenda, draftAgendaFromBrief, draftPersona, fetchReadiness, resolveAgendaItems,
  type AgendaItemDraft, type PersonaDraft, type PlaybookReadiness,
} from '../services/playbookPersonaApi';
import { apiService, getObjectionsGrouped, getFAQsGrouped, getLandmines, getCompetitors, searchDictionary, getSalesPlaybookOptions, type SalesPlaybookOption } from '../services/apiService';
import { getPlaybookNameLabel } from '../constants/labels';
import { TopicCategory, TOPIC_CATEGORY_LABELS, COMPETITOR_TYPE_LABELS } from '../constants/topicCategories';
import {
  DifficultyLevel,
  DIFFICULTY_LABELS,
  TrackType,
  TRACK_TYPE_HELPER_TEXT,
  TRACK_TYPE_OPTION_LABELS,
  OPENING_LINE_INVALID,
} from '../constants/trainingConstants';
import { validateTrainingTrackForm, buildTrainingTrackPayload } from '../utils/trainingTrackValidation';

interface TrackItem {
  itemId: string;
  type: 'objection' | 'faq' | 'landmine' | 'competitor';
  contentId: string;
  order: number;
  contentSnapshot: any;
}

interface PlaybookConfigFormData {
  enabled: boolean;
  // Wire key of the trainingtracks contract: the backend and
  // training_simulation_service still read `playbookConfig.industry`
  // (it holds the selected playbookName value). Renaming this key is a
  // coordinated cross-repo follow-up; do not change it here alone.
  industry: string;
  roleName: string;
}

interface TrackFormData {
  trackName: string;
  description: string;
  // kb tracks carry items[] and no knobs; playbook tracks carry
  // playbookConfig, the AI customer and the single difficulty knob.
  trackType: 'kb' | 'playbook';
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedDuration: number;
  tags: string[];
  items: TrackItem[];
  assignedUsers: string[];
  playbookConfig: PlaybookConfigFormData;
}

interface SortableItemProps {
  item: TrackItem;
  onDelete: (itemId: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ item, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.itemId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card ref={setNodeRef} style={style} sx={{ mb: 1 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <IconButton {...attributes} {...listeners} size="small">
          <DragIcon />
        </IconButton>

        <Box flex={1}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            {/* Item type is a category, not a status: one neutral chip. */}
            <Chip label={item.type} size="small" variant="outlined" />
          </Stack>
          <Typography variant="body2">
            {item.contentSnapshot?.questions?.[0] ||
             item.contentSnapshot?.question ||
             item.contentSnapshot?.triggers?.[0] ||
             item.contentSnapshot?.trigger ||
             item.contentSnapshot?.name ||
             'Unknown item (missing content data)'}
          </Typography>
          {item.contentSnapshot?.questions && item.contentSnapshot.questions.length > 1 && (
            <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
              +{item.contentSnapshot.questions.length - 1} variations
            </Typography>
          )}
          {item.contentSnapshot?.triggers && item.contentSnapshot.triggers.length > 1 && (
            <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
              +{item.contentSnapshot.triggers.length - 1} trigger variations
            </Typography>
          )}
          {(!item.contentSnapshot?.questions && !item.contentSnapshot?.question && !item.contentSnapshot?.triggers && !item.contentSnapshot?.trigger && !item.contentSnapshot?.name) && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              ContentSnapshot: {JSON.stringify(item.contentSnapshot)}
            </Typography>
          )}
        </Box>

        <IconButton size="small" color="error" onClick={() => onDelete(item.itemId)}>
          <DeleteIcon />
        </IconButton>
      </CardContent>
    </Card>
  );
};

/**
 * A playbook track's agenda lives in formData.items while editing so it
 * renders in the very same sortable list as a knowledge-base track. On save it
 * moves to personaConfig.agendaItems and items[] is sent empty.
 */
const agendaToTrackItems = (agenda: AgendaItemDraft[]): TrackItem[] =>
  agenda.map((agendaItem, order) => ({
    itemId: agendaItem.itemId,
    type: agendaItem.type,
    contentId: agendaItem.contentId,
    order,
    contentSnapshot: agendaItem.contentSnapshot || {},
  }));

/**
 * What a save sends. A playbook track saves its AI customer, with the agenda
 * moved out of items[]; a kb track never carries one. Publish uses the same
 * shape to decide whether it can be pressed, so the button and the save never
 * disagree about what is missing.
 */
const assembleTrackForSave = (formData: TrackFormData, persona: PersonaDraft | null) => (
  formData.trackType === 'playbook' && persona
    ? {
        ...formData,
        items: [],
        personaConfig: {
          ...persona,
          targetDurationMinutes: formData.estimatedDuration || 20,
          agendaItems: formData.items.map((item, order) => ({
            itemId: item.itemId,
            type: item.type,
            contentId: item.contentId,
            order,
            mustRaise: true,
          })),
        },
      }
    : formData
);

const TrainingTrackBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { trackId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(trackId);
  const { user } = useAppSelector((state) => state.auth);
  const currentUserUid = user?.uid ?? '';

  // Create-track flow: the popup on the tracks list pre-selects a type via
  // ?trackType=kb|playbook. Ignored in edit mode — loadTrackData overwrites it.
  const preselectedTrackType = searchParams.get('trackType');
  const initialTrackType: 'kb' | 'playbook' = preselectedTrackType === 'playbook' ? 'playbook' : 'kb';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<TrackFormData>({
    trackName: '',
    description: '',
    trackType: initialTrackType,
    difficulty: 'medium',
    estimatedDuration: 10,
    tags: [],
    items: [],
    assignedUsers: user?.uid ? [user.uid] : [],
    playbookConfig: {
      // A playbook track always runs on a playbook; there is no opt-out.
      enabled: initialTrackType === 'playbook',
      industry: '',
      roleName: '',
    },
  });

  const [tagInput, setTagInput] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [playbookOptions, setPlaybookOptions] = useState<SalesPlaybookOption[]>([]);

  // ── Playbook Live authoring (playbook tracks only; kb tracks never touch it)
  const [brief, setBrief] = useState('');
  const [persona, setPersona] = useState<PersonaDraft | null>(null);
  // Why the server refused the opening line, shown on that field until it is edited.
  const [openingLineError, setOpeningLineError] = useState<string | null>(null);
  const handlePersonaChange = (next: PersonaDraft) => {
    if (next?.openingLine !== persona?.openingLine) setOpeningLineError(null);
    setPersona(next);
  };
  const [agendaWarning, setAgendaWarning] = useState('');
  const [personaStep, setPersonaStep] = useState<'idle' | 'persona' | 'agenda'>('idle');
  // Scroll to the AI customer once, when a build produces it; never on reopen.
  const aiCustomerRef = useRef<HTMLDivElement>(null);
  const shouldScrollToCustomerRef = useRef(false);
  const [isInstructionsPreviewOpen, setIsInstructionsPreviewOpen] = useState(false);
  const [readiness, setReadiness] = useState<PlaybookReadiness | null>(null);

  // Item selection dialog
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemType, setItemType] = useState<'objection' | 'faq' | 'landmine' | 'competitor'>('objection');
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [currentItemPage, setCurrentItemPage] = useState(1);
  const [totalItemPages, setTotalItemPages] = useState(1);
  const [loadingItems, setLoadingItems] = useState(false);
  const [addingItems, setAddingItems] = useState(false);
  const itemsPerPage = 25;

  // Search state for item dialog
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [isItemSearchMode, setIsItemSearchMode] = useState(false);
  const [itemSearchResults, setItemSearchResults] = useState<any[]>([]);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);

  // User assignment
  const [users, setUsers] = useState<any[]>([]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load track data if editing
  useEffect(() => {
    if (isEditMode && trackId) {
      loadTrackData(trackId);
    }
    // loadTrackData is a plain function declared below this effect, so listing it would reference
    // the binding before initialisation. It is called with the trackId already in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, trackId]);

  // Load users for assignment + playbook options
  useEffect(() => {
    loadUsers();
    loadPlaybookOptions();
  }, []);

  const loadTrackData = async (id: string) => {
    try {
      setLoading(true);
      const response = await apiService.get(`/training/tracks/${id}`);
      if (response.success && response.data) {
        const track = response.data;
        setFormData({
          trackName: track.trackName,
          description: track.description,
          trackType: track.trackType || 'kb',
          difficulty: track.difficulty || 'medium',
          estimatedDuration: track.estimatedDuration,
          tags: track.tags || [],
          items: track.items || [],
          assignedUsers: currentUserUid
            ? [...new Set([currentUserUid, ...(track.assignedUsers || [])])]
            : track.assignedUsers || [],
          playbookConfig: {
            enabled: (track.trackType || 'kb') === 'playbook' || (track.playbookConfig?.enabled ?? false),
            industry: track.playbookConfig?.industry ?? '',
            roleName: track.playbookConfig?.roleName ?? '',
          },
        });

        // A saved Playbook Live track restores its AI customer so the middle
        // tab opens on what exists rather than an empty dictation box.
        if (track.personaConfig?.aiCustomerRole) {
          setPersona(track.personaConfig);
          await restoreAgenda(track.personaConfig?.agendaItems || []);
        }
      }
    } catch (error: any) {
      setError(error.error || error.message || 'Failed to load track');
    } finally {
      setLoading(false);
    }
  };

  // Saved agenda entries hold only ids; fetch their KB text so the list shows
  // the actual objection / FAQ / landmine / competitor, as a kb track does.
  const restoreAgenda = async (savedItems: any[]) => {
    let resolved: Awaited<ReturnType<typeof resolveAgendaItems>> = [];
    try {
      resolved = await resolveAgendaItems(
        savedItems.map((saved) => ({ contentId: String(saved?.contentId), type: saved?.type }))
      );
    } catch (resolveError: any) {
      console.error('Error loading agenda item content:', resolveError);
      setError('We could not load the text of this track\'s items. You can still reorder, remove or save them.');
    }
    const snapshotById = new Map(resolved.map((entry) => [`${entry.type}:${entry.contentId}`, entry.contentSnapshot]));
    const restoredItems: TrackItem[] = savedItems.map((saved, order) => ({
      itemId: saved?.itemId,
      type: saved?.type,
      contentId: String(saved?.contentId),
      order,
      contentSnapshot: snapshotById.get(`${saved?.type}:${String(saved?.contentId)}`) || {},
    }));
    setFormData((prev) => ({ ...prev, items: restoredItems }));
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.get('/admin/users');
      if (response.success && response.data) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadPlaybookOptions = async () => {
    try {
      const fetchedOptions = await getSalesPlaybookOptions();
      setPlaybookOptions(fetchedOptions);
    } catch (error) {
      console.error('Error loading playbook options:', error);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFormData((prev) => {
        const items = [...prev.items];
        const oldIndex = items.findIndex((item) => item.itemId === active.id);
        const newIndex = items.findIndex((item) => item.itemId === over.id);

        const reorderedItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          order: index,
        }));

        return { ...prev, items: reorderedItems };
      });
    }
  };

  const loadAvailableItems = async (type: 'objection' | 'faq' | 'landmine' | 'competitor', page: number = 1) => {
    setLoadingItems(true);
    try {
      let items: any[] = [];
      let totalPages = 1;

      switch (type) {
        case 'objection':
          const objResponse = await getObjectionsGrouped(page, itemsPerPage);
          // Only show original objections (not variations)
          items = objResponse.objections.filter(item => item.isOriginal === true);
          totalPages = objResponse.pagination?.totalPages || 1;
          break;
        case 'faq':
          const faqResponse = await getFAQsGrouped(page, itemsPerPage);
          // Only show original FAQs (not variations)
          items = faqResponse.faqs.filter(item => item.isOriginal === true);
          totalPages = faqResponse.pagination?.totalPages || 1;
          break;
        case 'landmine':
          // Only show compliance landmines (legal_risk), not detractors
          // Detractors are things the salesperson should avoid saying — not something a customer would bring up
          const landmineResponse = await getLandmines(page, itemsPerPage, { landmineType: 'legal_risk' });
          items = landmineResponse.landmines;
          totalPages = landmineResponse.pagination?.totalPages || 1;
          break;
        case 'competitor':
          const compResponse = await getCompetitors(page, itemsPerPage);
          items = compResponse.competitors;
          totalPages = compResponse.pagination?.totalPages || 1;
          break;
      }

      setAvailableItems(items);
      setTotalItemPages(totalPages);
      setCurrentItemPage(page);
    } catch (error) {
      console.error('Error loading items:', error);
      setError('Failed to load items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleItemSearch = async (query: string, page: number = 1) => {
    // Only perform search for objections and FAQs
    if (itemType !== 'objection' && itemType !== 'faq') {
      return;
    }

    if (!query.trim()) {
      // Empty search - exit search mode
      setIsItemSearchMode(false);
      setItemSearchResults([]);
      setItemSearchTerm('');
      return;
    }

    // Don't search if query is too short
    if (query.trim().length < 2) {
      return;
    }

    setItemSearchLoading(true);

    try {
      const type = itemType === 'objection' ? 'objection' : 'faq';
      const response = await searchDictionary(query, type, page, itemsPerPage);

      if (response.success) {
        // Filter to only show originals for consistency with regular loading
        const originals = response.results.filter(item => item.isOriginal === true);
        console.log('Search results:', originals.map(o => ({ id: o._id, question: o.question })));
        console.log('Current selectedItems:', selectedItems);
        setItemSearchResults(originals);
        setTotalItemPages(response.pagination.totalPages || 1);
        setCurrentItemPage(page);
        setIsItemSearchMode(true);
      } else {
        // Don't set global error for search issues in modal
        console.error('Search failed:', response.error);
        setIsItemSearchMode(false);
      }
    } catch (err: any) {
      // Don't set global error for search issues in modal
      console.error('Failed to search:', err);
      setIsItemSearchMode(false);
    } finally {
      setItemSearchLoading(false);
    }
  };

  // Debounced search effect for item dialog
  useEffect(() => {
    // Only set up debouncing for objections and FAQs
    if (!itemDialogOpen || (itemType !== 'objection' && itemType !== 'faq')) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      if (itemSearchTerm.trim()) {
        handleItemSearch(itemSearchTerm, 1);
      } else {
        setIsItemSearchMode(false);
        setItemSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
    // handleItemSearch is a plain function rebuilt on every render; listing it would restart the
    // 300ms debounce on every render. Its inputs are already dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSearchTerm, itemType, itemDialogOpen]);

  const handleOpenItemDialog = (type: 'objection' | 'faq' | 'landmine' | 'competitor') => {
    setItemType(type);
    setItemDialogOpen(true);

    // Pre-select items that are already in the track for this type
    const existingContentIds = formData.items
      .filter((item) => item.type === type)
      .map((item) => item.contentId);

    setSelectedItems(existingContentIds);

    // Reset search state
    setItemSearchTerm('');
    setIsItemSearchMode(false);
    setItemSearchResults([]);

    setCurrentItemPage(1);
    loadAvailableItems(type, 1);
  };

  const handleItemPageChange = (newPage: number) => {
    if (isItemSearchMode && itemSearchTerm.trim()) {
      handleItemSearch(itemSearchTerm, newPage);
    } else {
      loadAvailableItems(itemType, newPage);
    }
  };

  const handleAddItems = async () => {
    setAddingItems(true);
    try {
      // Get currently existing items of this type in the track
      const existingItemsOfType = formData.items.filter((item) => item.type === itemType);
      const existingContentIds = existingItemsOfType.map((item) => item.contentId);

      // Find items to add (newly selected)
      const itemsToAdd = selectedItems.filter((id) => !existingContentIds.includes(id));

      // Keep all existing items in their original positions, but remove unchecked ones of this type
      let updatedItems = formData.items.filter((item) =>
        item.type !== itemType || selectedItems.includes(item.contentId)
      );

      // Create new items for newly selected content
      // Fetch minimal display info only (not full snapshot - backend will fetch fresh content at session start)
      const newItems = await Promise.all(
        itemsToAdd.map(async (contentId, index) => {
          let content = availableItems.find((item) => item._id === contentId);

          // If content not in current page, fetch it for display purposes only
          if (!content) {
            try {
              let response: any;
              switch (itemType) {
                case 'objection':
                  response = await apiService.get(`/dictionary/objections/${contentId}`);
                  content = response.success ? response.data : response;
                  break;
                case 'faq':
                  response = await apiService.get(`/dictionary/faqs/${contentId}`);
                  content = response.success ? response.data : response;
                  break;
                case 'landmine':
                  response = await apiService.get(`/landmines/${contentId}`);
                  // Handle landmine response structure (uses 'landmine' field)
                  content = response.success ? (response.landmine || response.data) : response;
                  break;
                case 'competitor':
                  response = await apiService.get(`/competitors/${contentId}`);
                  content = response.success ? response.data : response;
                  break;
              }
            } catch (error) {
              console.error('Failed to fetch content for contentId:', contentId, error);
              return null;
            }
          }

          // Validate content exists
          if (!content) {
            console.error('Content not found for contentId:', contentId);
            return null;
          }

          // Build minimal display snapshot (only for UI display, not saved to backend)
          // Backend will fetch fresh content at session start
          let contentSnapshot: any = {};

          switch (itemType) {
            case 'objection':
            case 'faq':
              // Only store basic display info (first question)
              contentSnapshot = {
                question: content.question || '',
                questions: content.variations && content.variations.length > 0
                  ? [content.question, ...content.variations.map((v: any) => v.question).filter(Boolean)]
                  : [content.question],
              };
              break;
            case 'landmine':
              // Only store basic display info (first trigger)
              contentSnapshot = {
                trigger: content.trigger || '',
                triggers: content.triggerVariations && content.triggerVariations.length > 0
                  ? [content.trigger, ...content.triggerVariations]
                  : [content.trigger],
              };
              break;
            case 'competitor':
              // Only store name for display
              contentSnapshot = {
                name: content.name || '',
              };
              break;
          }

          return {
            itemId: `${Date.now()}-${index}`,
            type: itemType,
            contentId,
            order: updatedItems.length + index,
            contentSnapshot, // Minimal snapshot for UI display only
          } as TrackItem;
        })
      );

      // Filter out null items and add to updated list
      const validNewItems = newItems.filter((item): item is TrackItem => item !== null);
      updatedItems = [...updatedItems, ...validNewItems];

      // Reorder all items
      updatedItems = updatedItems.map((item, index) => ({
        ...item,
        order: index,
      }));

      setFormData((prev) => ({
        ...prev,
        items: updatedItems,
      }));

      setItemDialogOpen(false);
      setSelectedItems([]);
    } catch (error) {
      console.error('Error adding items:', error);
      setError('Failed to add items. Please try again.');
    } finally {
      setAddingItems(false);
    }
  };

  const handleDeleteItem = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.itemId !== itemId).map((item, index) => ({
        ...item,
        order: index,
      })),
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleDeleteTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  // ── Playbook Live: dictate → synthesise → review ────────────────────────
  useEffect(() => {
    if (formData.trackType !== 'playbook') return;
    let cancelled = false;
    (async () => {
      try {
        const latest = await fetchReadiness();
        if (!cancelled) setReadiness(latest);
      } catch {
        // A failed check must not lock an admin out of their own builder —
        // the server refuses synthesis independently.
        if (!cancelled) setReadiness(null);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.trackType]);

  useEffect(() => {
    if (!persona || !shouldScrollToCustomerRef.current) return;
    shouldScrollToCustomerRef.current = false;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    aiCustomerRef.current?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  }, [persona]);

  const applyAgenda = (result: Awaited<ReturnType<typeof draftAgenda>>) => {
    setFormData((prev) => ({ ...prev, items: agendaToTrackItems(result.agenda) }));
    setAgendaWarning(result.warning);
  };

  const handleGeneratePersona = async () => {
    setError(null);
    setPersonaStep('persona');
    const duration = formData.estimatedDuration || 20;
    // Both start now. The agenda is chosen from the description rather than the
    // finished persona, so it does not wait ~12s for synthesis.
    const agendaRequest = draftAgendaFromBrief(brief, duration);
    try {
      const generated = await draftPersona(brief, duration);
      shouldScrollToCustomerRef.current = true;
      setPersona(generated);
    } catch (generateError: any) {
      agendaRequest.catch(() => undefined); // the persona failed; its sibling no longer matters
      setPersonaStep('idle');
      // A 409 means the tenant stopped being ready since the page loaded:
      // re-check so the gate appears instead of a bare error.
      try {
        const latest = await fetchReadiness();
        setReadiness(latest);
        if (!latest.ready) return;
      } catch { /* fall through to the plain message */ }
      setError(generateError?.message || 'We could not build the customer. Try again.');
      return;
    }

    setPersonaStep('agenda');
    try {
      applyAgenda(await agendaRequest);
    } catch (agendaError: any) {
      setError(
        agendaError?.message
          || 'The customer is ready, but we could not choose their knowledge base items. Use Choose again.'
      );
    } finally {
      setPersonaStep('idle');
    }
  };

  const handleRegenerateAgenda = async () => {
    if (!persona) return;
    setError(null);
    try {
      setPersonaStep('agenda');
      applyAgenda(await draftAgenda(persona, persona.suggestedItemCount));
    } catch (regenerateError: any) {
      setError(regenerateError?.message || 'We could not re-choose the concerns.');
    } finally {
      setPersonaStep('idle');
    }
  };


  // A new example replaces the description, so the customer built from the old
  // one (and the items chosen for it) no longer match and are cleared.
  const handleExampleApplied = () => {
    setPersona(null);
    setOpeningLineError(null);
    setAgendaWarning('');
    setFormData((prev) => ({ ...prev, items: [] }));
  };

  // Why Publish cannot be pressed yet, or null when every required field is set.
  const publishBlocker = validateTrainingTrackForm(assembleTrackForSave(formData, persona) as any);
  const isPublishDisabled = loading || personaStep !== 'idle' || Boolean(publishBlocker);

  const handleSave = async (publish: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const formToSave = assembleTrackForSave(formData, persona);

      const validationError = validateTrainingTrackForm(formToSave as any);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Strip contentSnapshot from items before sending to backend
      // Backend will fetch fresh content at session start
      const payload = buildTrainingTrackPayload(formToSave as any, publish);

      let response;
      if (isEditMode && trackId) {
        response = await apiService.put(`/training/tracks/${trackId}`, payload);
      } else {
        response = await apiService.post('/training/tracks', payload);
      }

      if (response.success) {
        setSuccess(publish ? 'Track published successfully!' : 'Track saved as draft');
        setTimeout(() => navigate('/training'), 1500);
      }
    } catch (error: any) {
      console.error('Error saving track:', error);
      if (error?.code === OPENING_LINE_INVALID) setOpeningLineError(error?.error || null);
      setError(error.error || error.message || 'Failed to save track');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title={isEditMode ? 'Edit Training Track' : 'Create Training Track'}>
      <Box className="p-2">
        <PageHeader
          title={isEditMode ? 'Edit Training Track' : 'Create Training Track'}
          backLabel="Back to Training"
          onBack={() => navigate('/training')}
          actions={(
            <>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => handleSave(false)}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Save Draft
            </Button>
            {/* A disabled button fires no hover events, so the span carries the tooltip. */}
            <Tooltip title={publishBlocker || ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<PublishIcon />}
                  onClick={() => handleSave(true)}
                  disabled={isPublishDisabled}
                >
                  {loading ? <CircularProgress size={24} /> : 'Publish Track'}
                </Button>
              </span>
            </Tooltip>
            </>
          )}
        />

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Tabs
          idPrefix="track-builder"
          ariaLabel="Track builder sections"
          value={String(activeTab)}
          onChange={(id) => setActiveTab(Number(id))}
          items={[
            { id: '0', label: 'Track Details', tutorialId: 'track-details-tab' },
            { id: '1', label: 'Training Items', tutorialId: 'training-items-tab' },
            { id: '2', label: 'User Assignment', tutorialId: 'user-assignment-tab' },
          ]}
        />

        {/* Tab 1: Track Details */}
        {activeTab === 0 && (
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Track Name"
                  value={formData.trackName}
                  onChange={(e) => setFormData({ ...formData, trackName: e.target.value })}
                  required
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                {/* Medium, like the Track Name and Description fields above it. */}
                <Dropdown
                  data-tutorial="track-type-select"
                  label="Track Type"
                  size="medium"
                  width="field"
                  value={formData.trackType}
                  onChange={(trackType) =>
                    setFormData({
                      ...formData,
                      trackType: trackType as TrackFormData['trackType'],
                      playbookConfig: { ...formData.playbookConfig, enabled: trackType === 'playbook' },
                    })
                  }
                  options={[
                    { value: TrackType.KB, label: TRACK_TYPE_OPTION_LABELS[TrackType.KB] },
                    { value: TrackType.PLAYBOOK, label: TRACK_TYPE_OPTION_LABELS[TrackType.PLAYBOOK] },
                  ]}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {TRACK_TYPE_HELPER_TEXT[formData.trackType === 'playbook' ? TrackType.PLAYBOOK : TrackType.KB]}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Estimated Duration (minutes, optional)"
                  type="number"
                  value={formData.estimatedDuration || ''}
                  onChange={(e) => setFormData({ ...formData, estimatedDuration: parseInt(e.target.value) || 0 })}
                  helperText="Leave empty if unsure. Duration depends on how the session goes"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Tags
                </Typography>
                <Box display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    placeholder="Add tag... (Press Enter)"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddTag();
                      }
                    }}
                  />
                  <Button onClick={handleAddTag} variant="outlined" size="small">
                    Add
                  </Button>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                      size="small"
                    />
                  ))}
                </Stack>
              </Grid>

            </Grid>
          </Paper>
        )}

        {/* Tab 2: Training Items */}
        {activeTab === 1 && (
          <Box>
            {/* AI Customer Simulation Settings */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                AI Customer Simulation
              </Typography>
              {formData.trackType === 'playbook' ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    During this training, an AI will play the role of a customer in a realistic sales conversation.
                    It will naturally bring up knowledge base items and playbook topics as part of a flowing dialogue.
                    The rep practices responding in real time and is scored on each item.
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <Dropdown
                        label="Difficulty"
                        size="medium"
                        width="field"
                        value={formData.difficulty}
                        onChange={(difficulty) =>
                          setFormData({ ...formData, difficulty: difficulty as TrackFormData['difficulty'] })
                        }
                        options={Object.values(DifficultyLevel).map((level) => ({
                          value: level,
                          label: DIFFICULTY_LABELS[level],
                        }))}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        One knob controls how hard the AI customer is to convince: how open it is to
                        what it hears, how accurate an answer must be to pass, and how many times it
                        pushes back before moving on when it isn't.
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  During this training, an AI customer reads out each knowledge base item below, one at a time,
                  in the order you set. Objections and FAQs are asked as a natural question, landmines are worked
                  into a sentence, and competitors are mentioned the way a buyer would. When the rep finishes
                  answering, the customer moves on to the next item, and the session ends after the last one.
                  Every answer is scored after the session against the stored guidance for that item.
                </Typography>
              )}
            </Paper>

            {/* ── Playbook tracks: sales playbook first, then the AI customer.
                Only this tab differs for a playbook track. Everything else in
                this builder — details, tags, assignment, save/publish — is
                shared with knowledge-base tracks. */}
            {formData.trackType === 'playbook' && (
              <Paper sx={{ p: 0, mb: 3 }}>
                <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">
                    Sales Playbook
                  </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Every playbook track runs on one of your sales playbooks. The AI customer expects a
                      pitch, waits for discovery questions, and tests whether the rep can close and set
                      next steps, all based on the playbook you select.
                    </Typography>

                      <Box sx={{ p: 2.5, bgcolor: 'var(--surface-subtle)', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        <Stack spacing={2}>
                          <Dropdown
                            label={getPlaybookNameLabel()}
                            width="field"
                            value={formData.playbookConfig.industry}
                            onChange={(selectedPlaybookName) => {
                              // Auto-select the first role of the chosen
                              // playbook (the only role, if there's just one).
                              const roles =
                                playbookOptions.find((opt) => opt.playbookName === selectedPlaybookName)?.roles || [];
                              setFormData((prev) => ({
                                ...prev,
                                playbookConfig: {
                                  ...prev.playbookConfig,
                                  // `industry` is the trainingtracks wire key (see PlaybookConfigFormData).
                                  industry: selectedPlaybookName,
                                  roleName: roles[0]?.name || '',
                                },
                              }));
                            }}
                            options={playbookOptions.map((opt) => ({
                              value: opt.playbookName,
                              label: opt.displayName || opt.playbookName || 'Untitled playbook',
                            }))}
                          />

                          {formData.playbookConfig.industry && (
                            <Dropdown
                              label="Customer Role"
                              width="field"
                              value={formData.playbookConfig.roleName}
                              onChange={(roleName) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  playbookConfig: { ...prev.playbookConfig, roleName },
                                }))
                              }
                              options={(playbookOptions.find((opt) => opt.playbookName === formData.playbookConfig.industry)?.roles || []).map(
                                (role) => ({ value: role.name, label: role.displayName || role.name })
                              )}
                            />
                          )}

                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            The rep is expected to deliver the whole playbook: the
                            opening pitch, the discovery questions, and the closing
                            and next-step points.
                          </Typography>
                        </Stack>
                      </Box>
                </Box>
              </Paper>
            )}

            {formData.trackType === 'playbook' && readiness && !readiness.ready && (
              <Paper sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" gutterBottom>Before you can build this track</Typography>
                <ReadinessGate readiness={readiness} />
              </Paper>
            )}

            {formData.trackType === 'playbook' && (!readiness || readiness.ready) && (
              <>
                <Paper sx={{ p: 3, mt: 2 }}>
                  <Typography variant="h6" gutterBottom>Describe the customer</Typography>
                  <PersonaDictation
                    brief={brief}
                    onBriefChange={setBrief}
                    onExampleApplied={handleExampleApplied}
                    disabled={personaStep !== 'idle'}
                  />
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={personaStep !== 'idle' ? <CircularProgress size={16} /> : undefined}
                      disabled={personaStep !== 'idle' || !brief.trim()}
                      onClick={handleGeneratePersona}
                    >
                      {personaStep === 'persona' ? 'Building the customer…'
                        : personaStep === 'agenda' ? 'Choosing knowledge base items…'
                        : persona ? 'Rebuild from this description' : 'Build the customer'}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {persona
                        ? 'Replaces the customer and the knowledge base items below with new ones. Nothing is saved until you save the track.'
                        : 'Takes about 15 seconds. The customer and the knowledge base items they will raise are built together.'}
                    </Typography>
                  </Box>
                </Paper>

                {persona && (
                  <>
                    <Paper ref={aiCustomerRef} sx={{ p: 3, mt: 2, scrollMarginTop: 16 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">The AI customer</Typography>
                        <Button size="small" onClick={() => setIsInstructionsPreviewOpen(true)}>
                          Preview what the AI is told
                        </Button>
                      </Box>
                      <PersonaReview
                        persona={persona}
                        onChange={handlePersonaChange}
                        disabled={personaStep !== 'idle'}
                        openingLineError={openingLineError}
                      />
                    </Paper>
                    <InstructionsPreviewDialog
                      open={isInstructionsPreviewOpen}
                      persona={persona}
                      difficulty={formData.difficulty}
                      onClose={() => setIsInstructionsPreviewOpen(false)}
                    />
                  </>
                )}
              </>
            )}

            {/* Knowledge Base Items — the same list for both track types. A
                playbook track's items are chosen from the customer description
                and can then be reordered, removed or added to by hand. */}
            {(formData.trackType === 'kb' || persona) && (
            <Paper sx={{ p: 0, mt: formData.trackType === 'playbook' ? 2 : 0 }}>
              <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  {`Knowledge Base Items (${formData.items.length})`}
                </Typography>
                {formData.trackType === 'playbook' && (
                  <Button size="small" disabled={personaStep !== 'idle'} onClick={handleRegenerateAgenda}>
                    Choose again
                  </Button>
                )}
              </Box>

              <Box sx={{ p: 3 }}>
                {formData.trackType === 'playbook' && agendaWarning && (
                  <Alert severity="warning" sx={{ mb: 2 }}>{agendaWarning}</Alert>
                )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Add items from your knowledge base that the AI customer will bring up during the conversation:
                      objections, FAQs, compliance landmines, and competitor mentions.
                    </Typography>

                    <Box data-tutorial="add-items-buttons" sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('objection')}>
                        Add objections
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('faq')}>
                        Add FAQs
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('landmine')}>
                        Add landmines
                      </Button>
                      <Button size="small" variant="outlined" onClick={() => handleOpenItemDialog('competitor')}>
                        Add competitors
                      </Button>
                    </Box>

                    <Box data-tutorial="items-list-area">
                      {formData.trackType === 'playbook' && personaStep === 'agenda' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }} role="status">
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">
                            Choosing the knowledge base items this customer will raise…
                          </Typography>
                        </Box>
                      ) : formData.items.length === 0 ? (
                        <Alert severity="info">
                          No items added yet. Use the buttons above to add objections, FAQs, landmines, or competitors
                          that the AI customer should bring up during the conversation.
                        </Alert>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={formData.items.map((item) => item.itemId)}
                            strategy={verticalListSortingStrategy}
                          >
                            {formData.items.map((item) => (
                              <SortableItem
                                key={item.itemId}
                                item={item}
                                onDelete={handleDeleteItem}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      )}
                    </Box>
              </Box>
            </Paper>
            )}
          </Box>
        )}

        {/* Tab 3: User Assignment */}
        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Assign Users to Track
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Select which users should have access to this training track. You are automatically included.
            </Typography>

            <List>
              {users.map((u) => {
                const isCurrentUser = u.firebaseUid === currentUserUid;
                return (
                  <ListItem key={u.firebaseUid || u._id}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.assignedUsers.includes(u.firebaseUid)}
                          disabled={isCurrentUser}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData((prev) => ({
                                ...prev,
                                assignedUsers: [...prev.assignedUsers, u.firebaseUid],
                              }));
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                assignedUsers: prev.assignedUsers.filter((uid) => uid !== u.firebaseUid),
                              }));
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1">
                            {u.name}{isCurrentUser ? ' (you)' : ''}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {u.email}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {users.length === 0 && (
              <Alert severity="info">
                No users available. Please create users first.
              </Alert>
            )}
          </Paper>
        )}

        {/* Item Selection Dialog */}
        <Dialog
          open={itemDialogOpen}
          onClose={() => setItemDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              height: '80vh',
              maxHeight: '80vh',
            }
          }}
        >
          <DialogTitle>Add {itemType.charAt(0).toUpperCase() + itemType.slice(1)}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
            {/* Search box - only for objections and FAQs */}
            {(itemType === 'objection' || itemType === 'faq') && (
              <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search ${itemType}s...`}
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    ),
                    endAdornment: itemSearchTerm && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setItemSearchTerm('');
                          setIsItemSearchMode(false);
                          setItemSearchResults([]);
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
                {isItemSearchMode && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={`Search Results: ${itemSearchResults.length} found`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Scrollable List Container */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 3 }}>
              {(loadingItems || itemSearchLoading) ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <List>
                    {(isItemSearchMode ? itemSearchResults : availableItems).map((item) => {
                      const isChecked = selectedItems.includes(item._id);
                      return (
                      <ListItem key={item._id} disablePadding>
                        <ListItemButton
                          dense
                          onClick={() => {
                            if (selectedItems.includes(item._id)) {
                              setSelectedItems(selectedItems.filter((id) => id !== item._id));
                            } else {
                              setSelectedItems([...selectedItems, item._id]);
                            }
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            edge="start"
                          />
                          <ListItemText
                            primary={item.question || item.trigger || item.name}
                            secondary={
                              <Box component="span" sx={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                                {(itemType === 'objection' || itemType === 'faq') && (
                                  <Chip
                                    label="Original"
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: '20px', fontSize: '0.7rem' }}
                                  />
                                )}
                                {item.category && (
                                  <Chip
                                    label={
                                      (itemType === 'objection' || itemType === 'faq')
                                        ? TOPIC_CATEGORY_LABELS[item.category as TopicCategory] || item.category
                                        : itemType === 'competitor'
                                        ? COMPETITOR_TYPE_LABELS[item.category as keyof typeof COMPETITOR_TYPE_LABELS] || item.category
                                        : item.category
                                    }
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: '20px', fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            slotProps={{
                              secondary: {
                                component: 'div'
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      );
                    })}
                  </List>
                  {(isItemSearchMode ? itemSearchResults : availableItems).length === 0 && (
                    <Alert severity="info">
                      {isItemSearchMode
                        ? `No results found for "${itemSearchTerm}". Try a different search term.`
                        : `No ${itemType}s available. Please create some first.`}
                    </Alert>
                  )}
                </>
              )}
            </Box>

            {/* Fixed Pagination at Bottom */}
            {totalItemPages > 1 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', py: 2, px: 3, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalItemPages}
                  page={currentItemPage}
                  onChange={(_, page) => handleItemPageChange(page)}
                  color="primary"
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setItemDialogOpen(false)} disabled={addingItems}>
              Cancel
            </Button>
            <Button
              onClick={handleAddItems}
              variant="contained"
              disabled={selectedItems.length === 0 || addingItems}
              startIcon={addingItems ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {addingItems ? 'Adding...' : `Add Selected (${selectedItems.length})`}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </MainLayout>
  );
};

export default TrainingTrackBuilder;
